import { useState } from 'react'
import { useQuery } from 'convex/react'
import { Eye, Users, Mail, MousePointerClick } from 'lucide-react'
import { api } from '../../lib/api'
import { Spinner } from '../ui/Spinner'

const RANGES = [
  { label: '7 days', days: 7 },
  { label: '30 days', days: 30 },
  { label: '90 days', days: 90 },
]

const card = 'rounded-xl border border-zinc-800 bg-zinc-900 p-5'

export function StatsTab() {
  const [days, setDays] = useState(7)
  const stats = useQuery(api.analytics.getPageStats, { days })

  return (
    <div className="space-y-5">
      <div className="flex justify-end">
        <select
          value={days}
          onChange={(e) => setDays(Number(e.target.value))}
          className="rounded-lg border border-zinc-700 bg-zinc-900 px-3 py-2 text-sm text-white outline-none focus:border-fuchsia-500"
        >
          {RANGES.map((r) => (
            <option key={r.days} value={r.days}>
              {r.label}
            </option>
          ))}
        </select>
      </div>

      {stats === undefined ? (
        <div className="flex justify-center py-16">
          <Spinner />
        </div>
      ) : stats === null ? (
        <p className="text-center text-sm text-zinc-500">No page yet.</p>
      ) : (
        <>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            <Metric icon={<Users className="h-4 w-4" />} label="Unique visitors" value={stats.totals.uniqueVisitors} />
            <Metric icon={<Eye className="h-4 w-4" />} label="Page views" value={stats.totals.pageViews} />
            <Metric icon={<MousePointerClick className="h-4 w-4" />} label="Clicks" value={stats.totals.clicks} />
            <Metric icon={<Mail className="h-4 w-4" />} label="Subscribers" value={stats.totals.subscribes} />
          </div>

          <div className={card}>
            <h3 className="mb-4 text-sm font-semibold text-zinc-300">Visitors</h3>
            <Sparkline series={stats.series} />
          </div>

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div className={card}>
              <h3 className="mb-3 text-sm font-semibold text-zinc-300">Top startups</h3>
              {stats.topStartups.length === 0 ? (
                <p className="text-sm text-zinc-500">No clicks yet.</p>
              ) : (
                <ul className="space-y-2">
                  {stats.topStartups.map((s) => (
                    <li key={s.name} className="flex justify-between text-sm">
                      <span className="text-zinc-200">{s.name}</span>
                      <span className="text-zinc-500">{s.clicks}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <div className={card}>
              <h3 className="mb-3 text-sm font-semibold text-zinc-300">Sources</h3>
              {stats.topReferrers.length === 0 ? (
                <p className="text-sm text-zinc-500">No referrers yet.</p>
              ) : (
                <ul className="space-y-2">
                  {stats.topReferrers.map((r) => (
                    <li key={r.host} className="flex justify-between text-sm">
                      <span className="text-zinc-200">{r.host}</span>
                      <span className="text-zinc-500">{r.count}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  )
}

function Metric({ icon, label, value }: { icon: React.ReactNode; label: string; value: number }) {
  return (
    <div className={card}>
      <div className="flex items-center gap-2 text-zinc-400">
        {icon}
        <span className="text-xs">{label}</span>
      </div>
      <div className="mt-2 text-3xl font-extrabold text-white">{value}</div>
    </div>
  )
}

function Sparkline({ series }: { series: { day: string; pageViews: number; uniqueVisitors: number }[] }) {
  if (series.length === 0) return <p className="text-sm text-zinc-500">No data yet.</p>
  const max = Math.max(1, ...series.map((s) => s.pageViews))
  return (
    <div className="flex h-32 items-end gap-1">
      {series.map((s) => (
        <div key={s.day} className="flex flex-1 flex-col items-center gap-1" title={`${s.day}: ${s.pageViews} views`}>
          <div
            className="w-full rounded-t bg-fuchsia-500/80"
            style={{ height: `${Math.max(2, (s.pageViews / max) * 100)}%` }}
          />
        </div>
      ))}
    </div>
  )
}
